import { useEffect, useState, FormEvent } from 'react';
import * as api from '../../api/client';
import { useServices } from '../../hooks/useServices';
import { Alert } from '../common/Alert';
import { Button } from '../common/Button';
import { Card } from '../common/Card';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { Field } from '../common/Field';
import { Input, Select, checkboxClass } from '../common/Input';
import { Modal } from '../common/Modal';
import { apiError } from '../../utils/apiError';
import { formatDateTime } from '../../utils/datetime';
import { ApiKey, ApiKeyCreated, ApiKeyScope } from '../../types';

/**
 * Personal access tokens. The token is shown once, in the dialog that made
 * it; afterwards the list only knows the prefix — which is all it needs to
 * tell keys apart.
 */
export function ApiKeysSection({ canWrite }: { canWrite: boolean }) {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState<ApiKey | null>(null);

  const load = () => api.getApiKeys().then(setKeys).catch(err => setError(apiError(err, 'Could not load API keys'))).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  async function handleRevoke() {
    if (!revoking) return;
    try {
      await api.revokeApiKey(revoking.id);
      setKeys(prev => prev.filter(k => k.id !== revoking.id));
    } catch (err) {
      setError(apiError(err, 'Could not revoke the key'));
    } finally {
      setRevoking(null);
    }
  }

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between gap-3 mb-1">
        <h2 className="text-lg font-semibold">API keys</h2>
        <Button size="sm" onClick={() => setCreating(true)}>Create key</Button>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        For scripts, n8n, Grafana and anything else that cannot sign in. Send one as{' '}
        <code className="font-mono text-xs">Authorization: Bearer pc_…</code>. Every use is written to the audit log.{' '}
        <a href="/api/docs" target="_blank" rel="noreferrer" className="underline hover:no-underline">API reference</a>
      </p>

      {error && <Alert tone="error">{error}</Alert>}

      {loading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading keys…</p>
      ) : keys.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">You have no API keys.</p>
      ) : (
        <ul className="space-y-3">
          {keys.map(key => (
            <li key={key.id} className="flex items-center justify-between gap-3 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-md">
              <div className="text-sm min-w-0">
                <p className="font-medium truncate">
                  {key.name}
                  <span className={`ml-2 inline-block px-1.5 py-0.5 rounded text-[0.65rem] font-semibold uppercase tracking-wider ${key.scope === 'write' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200'}`}>
                    {key.scope}
                  </span>
                  {key.service_ids && (
                    <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                      {key.service_ids.length} service{key.service_ids.length === 1 ? '' : 's'}
                    </span>
                  )}
                </p>
                <p className="text-gray-500 dark:text-gray-400 text-xs font-mono tabular-nums truncate">
                  {key.prefix}… · {key.last_used_at ? `used ${formatDateTime(key.last_used_at)}` : 'never used'}
                  {key.expires_at ? ` · expires ${formatDateTime(key.expires_at)}` : ''}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setRevoking(key)}>Revoke</Button>
            </li>
          ))}
        </ul>
      )}

      <CreateKeyDialog open={creating} canWrite={canWrite} onClose={() => setCreating(false)} onCreated={() => load()} />

      <ConfirmDialog
        open={!!revoking}
        title="Revoke API key"
        message={`"${revoking?.name}" stops working immediately. Anything still using it will get 401 on its next request.`}
        confirmLabel="Revoke key"
        danger
        onConfirm={handleRevoke}
        onClose={() => setRevoking(null)}
      />
    </Card>
  );
}

const EXPIRY_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Never' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: '365', label: '1 year' },
];

function CreateKeyDialog({ open, canWrite, onClose, onCreated }: { open: boolean; canWrite: boolean; onClose: () => void; onCreated: () => void }) {
  const { data: services } = useServices();
  const [name, setName] = useState('');
  const [scope, setScope] = useState<ApiKeyScope>('read');
  const [expiry, setExpiry] = useState('');
  const [limitServices, setLimitServices] = useState(false);
  const [serviceIds, setServiceIds] = useState<number[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState<ApiKeyCreated | null>(null);
  const [copied, setCopied] = useState(false);

  function reset() {
    setName(''); setScope('read'); setExpiry(''); setLimitServices(false); setServiceIds([]);
    setError(''); setCreated(null); setCopied(false);
  }

  function close() {
    onClose();
    // Let the dialog finish closing before the token disappears from the DOM.
    setTimeout(reset, 200);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const key = await api.createApiKey({
        name: name.trim(),
        scope,
        ...(expiry ? { expires_in_days: parseInt(expiry, 10) } : {}),
        ...(limitServices && serviceIds.length ? { service_ids: serviceIds } : {}),
      });
      setCreated(key);
      onCreated();
    } catch (err) {
      setError(apiError(err, 'Could not create the key'));
    } finally {
      setSubmitting(false);
    }
  }

  async function copy() {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.token);
      setCopied(true);
    } catch {
      // The token is selectable below; nothing more to do.
    }
  }

  return (
    <Modal open={open} onClose={close} title={created ? 'API key created' : 'Create API key'}>
      {created ? (
        <div className="space-y-4">
          <Alert tone="success">
            Copy the token now. It is shown once and is not stored anywhere in PulseCheck.
          </Alert>
          <div className="flex items-stretch gap-2">
            <code
              className="flex-1 min-w-0 px-3 py-2 rounded-md bg-gray-100 dark:bg-gray-900 text-sm font-mono break-all select-all"
              data-testid="api-key-token"
            >
              {created.token}
            </code>
            <Button variant="secondary" onClick={copy}>{copied ? 'Copied' : 'Copy'}</Button>
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
            <p>Try it:</p>
            <pre className="p-3 rounded-md bg-gray-100 dark:bg-gray-900 text-xs font-mono overflow-x-auto">
              {`curl -H "Authorization: Bearer ${created.token}" ${window.location.origin}/api/v1/services`}
            </pre>
          </div>
          <div className="flex justify-end">
            <Button onClick={close}>Done</Button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <Alert tone="error">{error}</Alert>}

          <Field label="Name" required hint="What will use it — “Grafana”, “deploy script”.">
            {p => <Input {...p} required maxLength={100} value={name} onChange={e => setName(e.target.value)} autoFocus />}
          </Field>

          <fieldset>
            <legend className="block text-sm font-medium mb-1">Scope</legend>
            <div className="space-y-2">
              <label className="flex items-start gap-2 text-sm">
                <input type="radio" name="scope" className="mt-0.5" checked={scope === 'read'} onChange={() => setScope('read')} />
                <span><span className="font-medium">Read</span> — GET only. Everything your role can see; nothing it can change.</span>
              </label>
              <label className={`flex items-start gap-2 text-sm ${canWrite ? '' : 'opacity-50'}`}>
                <input type="radio" name="scope" className="mt-0.5" disabled={!canWrite} checked={scope === 'write'} onChange={() => setScope('write')} />
                <span><span className="font-medium">Write</span> — everything your role can do.{canWrite ? '' : ' Your role cannot change anything, so it cannot hold a write key.'}</span>
              </label>
            </div>
          </fieldset>

          <Field label="Expires" hint="An expired key returns 401 until it is revoked and replaced.">
            {p => (
              <Select {...p} value={expiry} onChange={e => setExpiry(e.target.value)}>
                {EXPIRY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </Select>
            )}
          </Field>

          <div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" className={checkboxClass} checked={limitServices} onChange={e => setLimitServices(e.target.checked)} />
              Limit to specific services
            </label>
            {limitServices && (
              <div className="mt-2 max-h-48 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700/60">
                {(services ?? []).map(s => (
                  <label key={s.id} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                    <input
                      type="checkbox"
                      className={checkboxClass}
                      checked={serviceIds.includes(s.id)}
                      onChange={e => setServiceIds(prev => e.target.checked ? [...prev, s.id] : prev.filter(id => id !== s.id))}
                    />
                    <span className="truncate">{s.name}</span>
                  </label>
                ))}
                {(services ?? []).length === 0 && <p className="px-3 py-2 text-sm text-gray-500">No services yet.</p>}
              </div>
            )}
            {limitServices && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                A limited key can only call <code className="font-mono">/api/v1/services</code> for the services ticked here.
              </p>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={close}>Cancel</Button>
            <Button type="submit" loading={submitting} disabled={!name.trim() || (limitServices && serviceIds.length === 0)}>
              {submitting ? 'Creating…' : 'Create key'}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
